import type { RoutePlan } from '@gpx-spa/geo-domain';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint';
import { hasPersistableRoutePlan } from '@/components/shipment/form/utils/shipment-form-route-composer-snapshot';
import type { RouteSyncMode } from '@/components/shipment/form/utils/persist/persistFormRoutes';

export type RoutePersistIntentMode = RouteSyncMode | 'skip';

export type RoutePersistIntent = {
  mode: RoutePersistIntentMode;
  routePlan: RoutePlan;
  selectedByPathId: Record<string, boolean>;
  corridorBufferM: number;
  geometryQualityPercent: number;
  stopsFingerprint: string;
};

function cloneRoutePlan(plan: RoutePlan): RoutePlan {
  return JSON.parse(JSON.stringify(plan)) as RoutePlan;
}

export function captureRoutePersistIntent(input: {
  mode: RoutePersistIntentMode;
  routePlan: RoutePlan | null;
  selectedByPathId: Record<string, boolean>;
  corridorBufferM: number;
  geometryQualityPercent: number;
  stops: ShipmentFormStopRow[];
}): RoutePersistIntent | null {
  if (input.mode === 'skip') return null;
  if (!hasPersistableRoutePlan(input.routePlan)) return null;

  const stopsFingerprint = computeStopsRouteFingerprint(input.stops);
  if (stopsFingerprint == null) return null;

  return {
    mode: input.mode,
    routePlan: cloneRoutePlan(input.routePlan!),
    selectedByPathId: { ...input.selectedByPathId },
    corridorBufferM: input.corridorBufferM,
    geometryQualityPercent: input.geometryQualityPercent,
    stopsFingerprint,
  };
}

export function resolveRoutePersistModeFromChangeSet(input: {
  stops: 'unchanged' | 'changed';
  stopsInvalidatesRoutes: boolean;
  routes: 'unchanged' | 'upsert' | 'recreate';
  routePlan: RoutePlan | null;
}): RoutePersistIntentMode {
  if (input.routes === 'recreate') return 'recreate';
  if (input.routes === 'upsert') return 'upsert';
  if (
    input.stops === 'changed' &&
    input.stopsInvalidatesRoutes &&
    hasPersistableRoutePlan(input.routePlan)
  ) {
    return 'recreate';
  }
  return 'skip';
}

/** Public API alias for route save mode from form change set. */
export const detectRouteSaveMode = resolveRoutePersistModeFromChangeSet;
